"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";
import Modal from "react-modal";

import {
  TbSearch,
  TbX,
  TbFilter,
  TbChevronDown,
  TbCheck,
  TbInfoCircle,
  TbCircleCheck,
  TbAlertCircle,
  TbUser,
} from "react-icons/tb";
import {
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

// Snackbar (igual padrão do DriverExpense)
function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Modal de confirmação "bonitinha" para Approve
 */
function ApproveConfirmModal({
  isOpen,
  onRequestClose,
  row,
  isSubmitting,
  onConfirm,
  formatCurrency,
}) {
  if (!row) return null;

  const driverLabel =
    row.driver_name || (row.driver_id ? `Driver #${row.driver_id}` : "—");

  const orderOrLoad =
    row.order_id
      ? `${row.order_id} / #${row.load_transport_id || "—"}`
      : row.load_transport_id
      ? `Load #${row.load_transport_id}`
      : "—";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSubmitting ? undefined : onRequestClose}
      contentLabel="Approve Factoring"
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-50"
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
    >
      <div className="bg-[#1c1b19] rounded-xl max-w-2xl w-full border border-zinc-700 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Approve Factoring</h2>
              <p className="text-xs text-white/60">
                This will update the status to{" "}
                <span className="text-white font-semibold">APPROVED</span>.
              </p>
            </div>
          </div>

          <button
            onClick={isSubmitting ? undefined : onRequestClose}
            className={`text-gray-400 hover:text-white transition-colors ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={isSubmitting ? "Aguarde..." : "Fechar"}
            type="button"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5">
          <div className="bg-black/20 border border-zinc-700 rounded-lg p-4 text-sm text-white/90 leading-relaxed">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Factoring ID
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  #{row.id}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Status
                </div>
                <div className="mt-1 text-sm font-semibold text-amber-200">
                  {String(row.status || "").toUpperCase()}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Driver
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {driverLabel}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Order / Load
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {orderOrLoad}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-zinc-900/30 border border-zinc-700 rounded-lg p-4">
                <div className="text-xs text-white/60 uppercase tracking-wider">
                  Original
                </div>
                <div className="mt-2 text-lg font-bold text-white">
                  {formatCurrency(row.original_amount)}
                </div>
                <div className="mt-1 text-xs text-white/60">
                  Payment Type:{" "}
                  <span className="text-white">
                    {row.payment_type || "—"}
                  </span>
                </div>
              </div>

              <div className="bg-emerald-900/10 border border-emerald-700/40 rounded-lg p-4">
                <div className="text-xs text-emerald-200 uppercase tracking-wider">
                  Factored
                </div>
                <div className="mt-2 text-lg font-bold text-emerald-200">
                  {formatCurrency(row.factored_amount)}
                </div>
                <div className="mt-1 text-xs text-emerald-100/80">
                  After approval
                </div>
              </div>

              <div className="bg-red-900/10 border border-red-700/40 rounded-lg p-4">
                <div className="text-xs text-red-200 uppercase tracking-wider">
                  Fee
                </div>
                <div className="mt-2 text-lg font-bold text-red-200">
                  {formatCurrency(row.fee_amount)}
                </div>
                <div className="mt-1 text-xs text-red-100/80">
                  {Number(row.fee_percent || 5)}%
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <ShieldCheck className="w-5 h-5 text-blue-300 mt-0.5" />
            <div className="text-xs text-white/80 leading-relaxed">
              By confirming, you acknowledge that this request will be marked as{" "}
              <span className="text-white font-semibold">approved</span> and can
              proceed to the payment workflow.
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={isSubmitting ? undefined : onRequestClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg border border-zinc-600 text-white hover:bg-white/5 transition-colors ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={isSubmitting ? undefined : onConfirm}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg bg-emerald-600 text-black font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></span>
                  Approving...
                </>
              ) : (
                "Yes, approve"
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Modal de confirmação para Reject (com notes opcional)
 */
function RejectConfirmModal({
  isOpen,
  onRequestClose,
  row,
  isSubmitting,
  onConfirm,
  formatCurrency,
  notes,
  setNotes,
}) {
  if (!row) return null;

  const driverLabel =
    row.driver_name || (row.driver_id ? `Driver #${row.driver_id}` : "—");

  const orderOrLoad =
    row.order_id
      ? `${row.order_id} / #${row.load_transport_id || "—"}`
      : row.load_transport_id
      ? `Load #${row.load_transport_id}`
      : "—";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSubmitting ? undefined : onRequestClose}
      contentLabel="Reject Factoring"
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-50"
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
    >
      <div className="bg-[#1c1b19] rounded-xl max-w-2xl w-full border border-zinc-700 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Reject Factoring</h2>
              <p className="text-xs text-white/60">
                This will update the status to{" "}
                <span className="text-white font-semibold">REJECTED</span>.
              </p>
            </div>
          </div>

          <button
            onClick={isSubmitting ? undefined : onRequestClose}
            className={`text-gray-400 hover:text-white transition-colors ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={isSubmitting ? "Aguarde..." : "Fechar"}
            type="button"
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5">
          <div className="bg-black/20 border border-zinc-700 rounded-lg p-4 text-sm text-white/90 leading-relaxed">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Factoring ID
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  #{row.id}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Status
                </div>
                <div className="mt-1 text-sm font-semibold text-amber-200">
                  {String(row.status || "").toUpperCase()}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Driver
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {driverLabel}
                </div>
              </div>

              <div className="border border-zinc-700 rounded-lg p-3 bg-zinc-900/20">
                <div className="text-[11px] text-white/60 uppercase tracking-wider">
                  Order / Load
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {orderOrLoad}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-zinc-900/30 border border-zinc-700 rounded-lg p-4">
                <div className="text-xs text-white/60 uppercase tracking-wider">
                  Original
                </div>
                <div className="mt-2 text-lg font-bold text-white">
                  {formatCurrency(row.original_amount)}
                </div>
                <div className="mt-1 text-xs text-white/60">
                  Payment Type:{" "}
                  <span className="text-white">
                    {row.payment_type || "—"}
                  </span>
                </div>
              </div>

              <div className="bg-emerald-900/10 border border-emerald-700/40 rounded-lg p-4">
                <div className="text-xs text-emerald-200 uppercase tracking-wider">
                  Factored
                </div>
                <div className="mt-2 text-lg font-bold text-emerald-200">
                  {formatCurrency(row.factored_amount)}
                </div>
                <div className="mt-1 text-xs text-emerald-100/80">
                  (still snapshot)
                </div>
              </div>

              <div className="bg-red-900/10 border border-red-700/40 rounded-lg p-4">
                <div className="text-xs text-red-200 uppercase tracking-wider">
                  Fee
                </div>
                <div className="mt-2 text-lg font-bold text-red-200">
                  {formatCurrency(row.fee_amount)}
                </div>
                <div className="mt-1 text-xs text-red-100/80">
                  {Number(row.fee_percent || 5)}%
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="text-xs text-white/80 leading-relaxed">
              Optional: add a note to explain why this request is being rejected.
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)..."
              className="mt-3 w-full min-h-[90px] bg-black/20 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-red-500"
              disabled={isSubmitting}
              maxLength={5000}
            />
            <div className="mt-1 text-[11px] text-white/50">
              {String(notes || "").length}/5000
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={isSubmitting ? undefined : onRequestClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg border border-zinc-600 text-white hover:bg-white/5 transition-colors ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={isSubmitting ? undefined : onConfirm}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Rejecting...
                </>
              ) : (
                "Yes, reject"
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function Factoring() {
  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  // ✅ Modal needs app element (same approach used in Inspection.jsx)
  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  // list
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination backend
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(20);

  // filters (server-side)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [statusFilter, setStatusFilter] = useState("pending");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // drivers filter custom (like DriverExpense)
  const [drivers, setDrivers] = useState([]);
  const [driveSearch, setDriveSearch] = useState("");
  const [selectedDrive, setSelectedDrive] = useState("all");
  const [showDrivesList, setShowDrivesList] = useState(false);
  const driveSearchRef = useRef(null);

  // sorting
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // KPI
  const [kpis, setKpis] = useState({
    totalRequests: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    cancelledCount: 0,
    totalOriginal: 0,
    totalFactored: 0,
    totalFee: 0,
  });

  // approve state
  const [approvingId, setApprovingId] = useState(null);

  // reject state
  const [rejectingId, setRejectingId] = useState(null);

  // ✅ approve confirm modal state
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveTargetRow, setApproveTargetRow] = useState(null);

  // ✅ reject confirm modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTargetRow, setRejectTargetRow] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  // snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getToken = () => localStorage.getItem("token");

  // debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatCurrency = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const s = String(dateString);
    const safe = s.length === 10 ? `${s}T12:00:00` : s;
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(safe).toLocaleDateString("en-US", options);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(String(dateString).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const dateOnly = (dt) => {
    if (!dt) return "";
    const s = String(dt);
    if (s.includes("T")) return s.split("T")[0];
    if (s.includes(" ")) return s.split(" ")[0];
    return s;
  };

  const userLabelFromApiUser = (u) => {
    if (!u) return null;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (u?.email) return u.email;
    if (u?.username) return u.username;
    return null;
  };

  const driverLabelFromDriverApi = (d) => {
    const u = d?.user;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (u?.email) return u.email;
    return `Driver #${d?.id ?? ""}`;
  };

  const fetchDrivers = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_ORIGIN}/api/driver`, {
        headers: {
          "Screen-Name": "/Factoring",
          Authorization: `Bearer ${token}`,
        },
      });

      const list =
        (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
        (Array.isArray(res?.data?.data) && res.data.data) ||
        (Array.isArray(res?.data) && res.data) ||
        [];

      const mapped = list.map((d) => ({
        id: d.id,
        name: driverLabelFromDriverApi(d),
      }));

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setDrivers(mapped);
    } catch (e) {
      setDrivers([]);
    }
  };

  const handleDriveSelect = (driveId, driveName) => {
    setSelectedDrive(driveId);
    setDriveSearch(driveName);
    setShowDrivesList(false);
    setCurrentPage(1);
  };

  const clearDriveFilter = () => {
    setSelectedDrive("all");
    setDriveSearch("");
    setShowDrivesList(false);
    setCurrentPage(1);
  };

  const handleDriveBlur = () => {
    setTimeout(() => {
      setShowDrivesList(false);
    }, 200);
  };

  const filteredDrives = useMemo(() => {
    return drivers.filter((d) =>
      d.name.toLowerCase().includes(driveSearch.toLowerCase())
    );
  }, [driveSearch, drivers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (driveSearchRef.current && !driveSearchRef.current.contains(event.target)) {
        setShowDrivesList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const mapFactoringFromApi = (f) => {
    const driver = f?.driver || null;
    const driverUser = driver?.user || null;
    const driverName = driverUser
      ? `${driverUser.first_name || ""} ${driverUser.last_name || ""}`.trim()
      : null;

    const loadTransport = f?.load_transport || f?.loadTransport || null;

    const requestedBy = f?.requested_by || f?.requestedBy || null;
    const processedBy = f?.processed_by || f?.processedBy || null;

    const requestedByName = userLabelFromApiUser(requestedBy) || null;
    const processedByName = userLabelFromApiUser(processedBy) || null;

    const createdAt = f?.created_at || f?.createdAt || "";
    const processedAt = f?.processed_at || f?.processedAt || null;

    const originalAmount = Number(f?.original_amount ?? f?.originalAmount ?? 0);
    const factoredAmount = Number(f?.factored_amount ?? f?.factoredAmount ?? 0);
    const feeAmount = Math.max(0, originalAmount - factoredAmount);

    return {
      id: String(f?.id ?? ""),
      original_id: f?.id,

      status: f?.status || "pending",
      payment_type: f?.payment_type || "",
      fee_percent: Number(f?.fee_percent ?? 5),
      original_amount: originalAmount,
      factored_amount: factoredAmount,
      fee_amount: feeAmount,

      driver_id: f?.driver_id ?? f?.driverId ?? driver?.id ?? null,
      driver_name: driverName || null,

      load_transport_id:
        f?.load_transport_id ?? f?.loadTransportId ?? loadTransport?.id ?? null,
      order_id: loadTransport?.order_id ?? loadTransport?.orderId ?? null,

      requested_by_user_id: f?.requested_by_user_id ?? null,
      requested_by_name: requestedByName,

      processed_by_user_id: f?.processed_by_user_id ?? null,
      processed_by_name: processedByName,
      processed_at: processedAt,

      notes: f?.notes ?? "",

      created_at: createdAt,
      date: dateOnly(createdAt),
      original: f,
    };
  };

  const parsePaginator = (apiPayload) => {
    // Esperado: { data: { current_page, data: [...], last_page, ... , kpis? }, ... }
    if (
      apiPayload?.data &&
      typeof apiPayload.data === "object" &&
      Array.isArray(apiPayload.data.data)
    ) {
      return apiPayload.data;
    }
    return null;
  };

  const buildServerParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage || 20,
      sort_by: sortBy || "created_at",
      sort_direction: sortDirection || "desc",
      with_kpis: 1,
    };

    const q = (debouncedSearchQuery || "").trim();
    if (q) params.q = q;

    if (statusFilter && statusFilter !== "all") params.status = statusFilter;
    else params.status = "all";

    if (paymentTypeFilter && paymentTypeFilter !== "all")
      params.payment_type = paymentTypeFilter;

    if (selectedDrive !== "all") params.driver_id = selectedDrive;

    if (dateFrom) params.date_from = `${dateFrom} 00:00:00`;
    if (dateTo) params.date_to = `${dateTo} 23:59:59`;

    return params;
  };

  const fetchFactorings = async (page = 1) => {
    setLoading(true);
    try {
      const token = getToken();

      const res = await axios.get(`${API_ORIGIN}/api/factoring/paginated`, {
        headers: {
          "Screen-Name": "/Factoring",
          Authorization: `Bearer ${token}`,
        },
        params: buildServerParams(page),
      });

      const paginator = parsePaginator(res.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      const mapped = list.map(mapFactoringFromApi);

      const cp = Number(paginator?.current_page ?? page) || 1;
      const lp = Number(paginator?.last_page ?? 1) || 1;
      const tt = Number(paginator?.total ?? mapped.length) || 0;
      const pp = Number(paginator?.per_page ?? perPage) || perPage;

      setItems(mapped);
      setCurrentPage(cp);
      setTotalPages(lp);
      setTotalItems(tt);
      setPerPage(pp);

      setRangeFrom(Number(paginator?.from ?? (mapped.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to ?? mapped.length) || 0);

      const serverKpis = paginator?.kpis;
      if (serverKpis && typeof serverKpis === "object") {
        setKpis({
          totalRequests: Number(serverKpis.totalRequests ?? 0),
          pendingCount: Number(serverKpis.pendingCount ?? 0),
          approvedCount: Number(serverKpis.approvedCount ?? 0),
          rejectedCount: Number(serverKpis.rejectedCount ?? 0),
          cancelledCount: Number(serverKpis.cancelledCount ?? 0),
          totalOriginal: Number(serverKpis.totalOriginal ?? 0),
          totalFactored: Number(serverKpis.totalFactored ?? 0),
          totalFee: Number(serverKpis.totalFee ?? 0),
        });
      } else {
        setKpis({
          totalRequests: mapped.length,
          pendingCount: mapped.filter((x) => x.status === "pending").length,
          approvedCount: mapped.filter((x) => x.status === "approved").length,
          rejectedCount: mapped.filter((x) => x.status === "rejected").length,
          cancelledCount: mapped.filter((x) => x.status === "cancelled").length,
          totalOriginal: mapped.reduce((s, x) => s + Number(x.original_amount || 0), 0),
          totalFactored: mapped.reduce((s, x) => s + Number(x.factored_amount || 0), 0),
          totalFee: mapped.reduce((s, x) => s + Number(x.fee_amount || 0), 0),
        });
      }
    } catch (e) {
      setItems([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);
      setKpis({
        totalRequests: 0,
        pendingCount: 0,
        approvedCount: 0,
        rejectedCount: 0,
        cancelledCount: 0,
        totalOriginal: 0,
        totalFactored: 0,
        totalFee: 0,
      });
      showSnackbar("Erro ao carregar factorings", "error");
    } finally {
      setLoading(false);
    }
  };

  // init
  useEffect(() => {
    (async () => {
      await fetchDrivers();
      await fetchFactorings(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch on server-filters/sort/perpage/page
  useEffect(() => {
    fetchFactorings(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    perPage,
    sortBy,
    sortDirection,
    debouncedSearchQuery,
    statusFilter,
    paymentTypeFilter,
    selectedDrive,
    dateFrom,
    dateTo,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("pending");
    setPaymentTypeFilter("all");
    setDateFrom("");
    setDateTo("");
    setDriveSearch("");
    setSelectedDrive("all");
    setShowDrivesList(false);

    setSortBy("created_at");
    setSortDirection("desc");
    setPerPage(20);

    setCurrentPage(1);
    showSnackbar("Filters cleared", "info");
  };

  // pagination
  const goToPage = (pageNumber) => {
    const target = Math.max(1, Math.min(Number(pageNumber) || 1, totalPages || 1));
    if (target === currentPage) return;
    setCurrentPage(target);
  };

  const nextPage = () => {
    if (currentPage >= totalPages) return;
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage <= 1) return;
    goToPage(currentPage - 1);
  };

  // ✅ Approve modal open
  const handleApprove = (row) => {
    if (!row?.original_id) return;
    setApproveTargetRow(row);
    setApproveModalOpen(true);
  };

  const closeApproveModal = () => {
    if (approvingId) return; // prevent close while submitting
    setApproveModalOpen(false);
    setApproveTargetRow(null);
  };

  const confirmApprove = async () => {
    const row = approveTargetRow;
    if (!row?.original_id) return;

    setApprovingId(String(row.id));

    try {
      const token = getToken();

      await axios.post(
        `${API_ORIGIN}/api/factoring/${row.original_id}/approve`,
        {},
        {
          headers: {
            "Screen-Name": "/Factoring",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      showSnackbar("Factoring approved successfully!", "success");
      closeApproveModal();
      await fetchFactorings(currentPage);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Erro ao aprovar factoring (verifique permissões).";
      showSnackbar(msg, "error");
    } finally {
      setApprovingId(null);
    }
  };

  // ✅ Reject modal open
  const handleReject = (row) => {
    if (!row?.original_id) return;
    setRejectTargetRow(row);
    setRejectNotes("");
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    if (rejectingId) return; // prevent close while submitting
    setRejectModalOpen(false);
    setRejectTargetRow(null);
    setRejectNotes("");
  };

  const confirmReject = async () => {
    const row = rejectTargetRow;
    if (!row?.original_id) return;

    setRejectingId(String(row.id));

    try {
      const token = getToken();

      const payload = {};
      const notes = String(rejectNotes || "").trim();
      if (notes) payload.notes = notes;

      await axios.post(
        `${API_ORIGIN}/api/factoring/${row.original_id}/reject`,
        payload,
        {
          headers: {
            "Screen-Name": "/Factoring",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      showSnackbar("Factoring rejected successfully!", "success");
      closeRejectModal();
      await fetchFactorings(currentPage);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Erro ao rejeitar factoring (verifique permissões).";
      showSnackbar(msg, "error");
    } finally {
      setRejectingId(null);
    }
  };

  const statusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved") return "bg-emerald-600/20 border-emerald-600/40 text-emerald-200";
    if (s === "pending") return "bg-amber-600/20 border-amber-600/40 text-amber-200";
    if (s === "rejected") return "bg-red-600/20 border-red-600/40 text-red-200";
    if (s === "cancelled") return "bg-zinc-600/20 border-zinc-600/40 text-zinc-200";
    return "bg-zinc-700/20 border-zinc-700/40 text-white/80";
  };

  const paymentTypeOptions = [
    { value: "all", label: "All Payment Types" },
    { value: "COD", label: "COD" },
    { value: "2 BD", label: "2 BD" },
    { value: "5 BD", label: "5 BD" },
    { value: "7 BD", label: "7 BD" },
    { value: "15 BD", label: "15 BD" },
    { value: "30 BD", label: "30 BD" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <AuthenticatedLayout>
      {/* ✅ Approve confirmation modal */}
      <ApproveConfirmModal
        isOpen={approveModalOpen}
        onRequestClose={closeApproveModal}
        row={approveTargetRow}
        isSubmitting={!!approvingId}
        onConfirm={confirmApprove}
        formatCurrency={formatCurrency}
      />

      {/* ✅ Reject confirmation modal */}
      <RejectConfirmModal
        isOpen={rejectModalOpen}
        onRequestClose={closeRejectModal}
        row={rejectTargetRow}
        isSubmitting={!!rejectingId}
        onConfirm={confirmReject}
        formatCurrency={formatCurrency}
        notes={rejectNotes}
        setNotes={setRejectNotes}
      />

      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="FACTORING" description="Review and approve/reject factoring requests" />
        </GroupedContainer>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Requests (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">
              {Number(kpis.totalRequests || 0)}
            </div>
            <div className="mt-2 text-xs text-white/70 flex flex-wrap gap-x-3 gap-y-1">
              <span>
                Approved: <span className="text-emerald-300 font-semibold">{Number(kpis.approvedCount || 0)}</span>
              </span>
              <span>
                Rejected: <span className="text-red-300 font-semibold">{Number(kpis.rejectedCount || 0)}</span>
              </span>
              <span>
                Cancelled: <span className="text-zinc-200 font-semibold">{Number(kpis.cancelledCount || 0)}</span>
              </span>
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Pending (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-amber-300">
              {Number(kpis.pendingCount || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Total Factored (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-emerald-300">
              {formatCurrency(kpis.totalFactored)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Total Fee (filtered)</div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-red-300">
              {formatCurrency(kpis.totalFee)}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <TbFilter className="text-amber-500 w-5 h-5" />
              Filters
            </h2>

            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              <TbX className="w-4 h-4" /> Clear Filters
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search (server-side) */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search (driver, status, order, load id...)"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            {/* Driver filter custom */}
            <div className="relative" ref={driveSearchRef}>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbSearch className="text-white text-lg" />
                </div>
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={driveSearch}
                  onChange={(e) => {
                    setDriveSearch(e.target.value);
                    if (e.target.value.trim() !== "") setShowDrivesList(true);
                  }}
                  onFocus={() => setShowDrivesList(true)}
                  onBlur={handleDriveBlur}
                  className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
                {(driveSearch || selectedDrive !== "all") && (
                  <button
                    onClick={clearDriveFilter}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  >
                    <TbX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showDrivesList && (
                <div className="absolute z-10 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
                  <div className="max-h-48 overflow-y-auto">
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDriveSelect("all", "")}
                      className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="px-2 py-1 text-center text-white text-sm">All Drivers</span>
                      </div>
                      {selectedDrive === "all" && <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />}
                    </div>

                    {filteredDrives.length > 0 ? (
                      <>
                        {filteredDrives.map((d) => (
                          <div
                            key={d.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDriveSelect(String(d.id), d.name)}
                            className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="px-2 py-1 text-center text-white text-sm" title={d.name}>
                                {d.name}
                              </span>
                            </div>
                            {selectedDrive === String(d.id) && (
                              <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        ))}
                        <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                          {driveSearch
                            ? `Found ${filteredDrives.length} of ${drivers.length} drivers`
                            : `Showing all ${filteredDrives.length} drivers`}
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-400 text-sm">No drivers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                           appearance-none"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Payment Type */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbUser className="text-gray-400 text-lg" />
              </div>
              <select
                value={paymentTypeFilter}
                onChange={(e) => {
                  setPaymentTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                           appearance-none"
              >
                {paymentTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Date range */}
            <div className="md:col-span-2 lg:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="relative">
                  <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">To</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    onFocus={(e) => e.target.showPicker?.()}
                    style={{ colorScheme: "dark" }}
                    className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>

            {/* Per page */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={String(perPage)}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="10" className="bg-[#1c1b19]">10 / page</option>
                <option value="20" className="bg-[#1c1b19]">20 / page</option>
                <option value="50" className="bg-[#1c1b19]">50 / page</option>
                <option value="100" className="bg-[#1c1b19]">100 / page</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Sort */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="created_at" className="bg-[#1c1b19]">Order by: Created</option>
                <option value="status" className="bg-[#1c1b19]">Order by: Status</option>
                <option value="payment_type" className="bg-[#1c1b19]">Order by: Payment Type</option>
                <option value="original_amount" className="bg-[#1c1b19]">Order by: Original Amount</option>
                <option value="factored_amount" className="bg-[#1c1b19]">Order by: Factored Amount</option>
                <option value="fee_percent" className="bg-[#1c1b19]">Order by: Fee %</option>
                <option value="id" className="bg-[#1c1b19]">Order by: ID</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Direction */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={sortDirection}
                onChange={(e) => {
                  setSortDirection(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="asc" className="bg-[#1c1b19]">Direction: ASC</option>
                <option value="desc" className="bg-[#1c1b19]">Direction: DESC</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>

          <div className="mt-3 text-xs text-white/70">
            Server pagination: page {currentPage} of {totalPages} • total {totalItems} • per page {perPage}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Payment Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Original</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Factored</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Fee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Driver</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Order Id / Load Id</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Processed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading factorings...</p>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((row) => {
                  const isApproving = String(approvingId) === String(row.id);
                  const isRejecting = String(rejectingId) === String(row.id);
                  const isBusy = isApproving || isRejecting;

                  const canActions = String(row.status).toLowerCase() === "pending";

                  return (
                    <tr key={row.id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">#{row.id}</td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(row.date)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full border text-xs ${statusBadge(
                            row.status
                          )}`}
                        >
                          {String(row.status || "").toUpperCase()}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.payment_type || "—"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        {formatCurrency(row.original_amount)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-emerald-300">
                        {formatCurrency(row.factored_amount)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-300">
                        {formatCurrency(row.fee_amount)}{" "}
                        <span className="text-white/50 text-xs">({Number(row.fee_percent || 5)}%)</span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <span title={row.driver_name || ""}>
                          {row.driver_name || (row.driver_id ? `Driver #${row.driver_id}` : "—")}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.order_id ? (
                          <span title={`Order ID: ${row.order_id} / LoadTransport ID: ${row.load_transport_id}`}>
                            {row.order_id} / #{row.load_transport_id}
                          </span>
                        ) : row.load_transport_id ? (
                          <span title={`LoadTransport ID: ${row.load_transport_id}`}>
                            Load #{row.load_transport_id}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.processed_at ? (
                          <div className="leading-tight">
                            <div className="text-white/90 text-xs">
                              {row.processed_by_name || (row.processed_by_user_id ? `User #${row.processed_by_user_id}` : "—")}
                            </div>
                            <div className="text-white/60 text-[11px]">
                              {formatDateTime(row.processed_at)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-white/50">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApprove(row)}
                            disabled={!canActions || isBusy}
                            className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                              !canActions
                                ? "border-zinc-700 text-white/40 cursor-not-allowed"
                                : isBusy
                                ? "border-emerald-700 text-emerald-200 opacity-60 cursor-not-allowed"
                                : "border-emerald-700 text-emerald-200 hover:bg-emerald-700/15"
                            }`}
                            title={canActions ? "Approve factoring" : "Only pending can be approved"}
                          >
                            {isApproving ? "Approving..." : "Approve"}
                          </button>

                          <button
                            onClick={() => handleReject(row)}
                            disabled={!canActions || isBusy}
                            className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                              !canActions
                                ? "border-zinc-700 text-white/40 cursor-not-allowed"
                                : isBusy
                                ? "border-red-700 text-red-200 opacity-60 cursor-not-allowed"
                                : "border-red-700 text-red-200 hover:bg-red-700/15"
                            }`}
                            title={canActions ? "Reject factoring" : "Only pending can be rejected"}
                          >
                            {isRejecting ? "Rejecting..." : "Reject"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-400">
                    <div className="text-gray-400">
                      <TbInfoCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No factorings found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1 || loading}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => goToPage(i)}
                        disabled={loading}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages || loading}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {rangeFrom}-{rangeTo} of {totalItems} factorings
            </div>
          </>
        )}
      </div>

      {snackbar.open && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />
      )}
    </AuthenticatedLayout>
  );
}